import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Navbar from "@/components/luxenova/Navbar";
import Footer from "@/components/luxenova/Footer";
import ChatbotWidget from "@/components/luxenova/ChatbotWidget";
import useSeo from "@/lib/useSeo";
import { useI18n } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Blog() {
    const { t, lang } = useI18n();
    const [posts, setPosts] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);

    useSeo({
        title:
            lang === "pl"
                ? "Dziennik LuxeNova — Luksusowe nieruchomości i lifestyle w Krakowie"
                : "LuxeNova Journal — Luxury Real Estate & Lifestyle in Kraków",
        description:
            lang === "pl"
                ? "Redakcyjne treści o luksusowych nieruchomościach w Polsce, relokacji do Krakowa i sztuce dobrego życia."
                : "Editorial insights on luxury real estate in Poland, relocation to Kraków and the art of living well.",
        canonical: typeof window !== "undefined" ? window.location.origin + "/blog" : undefined,
    });

    useEffect(() => {
        (async () => {
            setLoading(true);
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (cat && cat !== "all") params.set("category", cat);
            const res = await fetch(`${API}/blog/posts?${params.toString()}`);
            if (res.ok) setPosts(await res.json());
            setLoading(false);
        })();
    }, [q, cat]);

    useEffect(() => {
        fetch(`${API}/blog/categories`)
            .then((r) => (r.ok ? r.json() : { categories: [] }))
            .then((d) => setCats(d.categories || []));
    }, []);

    const showEmpty = useMemo(() => !loading && posts.length === 0, [loading, posts]);

    return (
        <div className="min-h-screen bg-[#050D1C] text-slate-200">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <div className="reveal in text-center max-w-3xl mx-auto">
                        <span className="eyebrow">{t("nav.journal")}</span>
                        <h1 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
                            {t("blog.title")}
                        </h1>
                        <p className="mt-6 text-slate-400 leading-relaxed">{t("blog.subtitle")}</p>
                    </div>

                    <div className="mt-14 flex flex-col md:flex-row items-stretch md:items-center gap-4 md:gap-6">
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A96E]" />
                            <input
                                data-testid="blog-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t("blog.search_placeholder")}
                                className="w-full pl-11 pr-4 py-3 bg-[#0A1A38]/60 border border-[#C9A96E]/25 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#C9A96E]/70 rounded-sm text-sm"
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <CatChip active={cat === "all"} onClick={() => setCat("all")} testId="blog-cat-all">
                                {t("blog.all")}
                            </CatChip>
                            {cats.map((c) => (
                                <CatChip key={c} active={cat === c} onClick={() => setCat(c)} testId={`blog-cat-${c}`}>
                                    {c}
                                </CatChip>
                            ))}
                        </div>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {posts.map((p) => (
                            <Link
                                to={`/blog/${p.slug}`}
                                key={p.slug}
                                data-testid={`blog-card-${p.slug}`}
                                className="glass-card overflow-hidden group flex flex-col"
                            >
                                {p.featured_image ? (
                                    <div className="h-48 overflow-hidden">
                                        <img
                                            src={p.featured_image}
                                            alt={p.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                                        />
                                    </div>
                                ) : null}
                                <div className="p-7 flex flex-col flex-1">
                                    <div className="flex items-center gap-3 text-[10px] tracking-[0.28em] uppercase text-[#C9A96E]">
                                        <span>{p.category}</span>
                                        <span className="text-slate-500">·</span>
                                        <span className="text-slate-500">{p.read_time_minutes} {t("blog.read_time")}</span>
                                    </div>
                                    <h3 className="mt-4 font-serif-display text-white text-2xl leading-tight">{p.title}</h3>
                                    <p className="mt-3 text-slate-400 text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                                    <div className="mt-6 pt-5 border-t border-[#C9A96E]/15 text-[11px] tracking-[0.28em] uppercase text-[#C9A96E] group-hover:text-white transition-colors">
                                        {t("blog.read_more")}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {showEmpty && (
                        <div data-testid="blog-empty" className="mt-16 text-center py-16 border border-dashed border-[#C9A96E]/20 rounded-sm">
                            <p className="text-slate-400">{t("blog.empty")}</p>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}

function CatChip({ active, onClick, testId, children }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-4 py-2 text-[11px] tracking-[0.24em] uppercase border rounded-sm transition ${
                active
                    ? "border-[#C9A96E] bg-[#C9A96E] text-[#050D1C]"
                    : "border-[#C9A96E]/25 text-slate-300 hover:border-[#C9A96E]/60 hover:text-[#C9A96E]"
            }`}
        >
            {children}
        </button>
    );
}
